import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from '../LoadingOverlay';
import PropertiesStore from '../stores/PropertiesStore';
import AddProperty from './AddProperty';
import PropertiesTable from './PropertiesTable';
import Property from './Property';

/*
 * Removed the loading of permissions on the inital load of this page,
 * instead permissions are loaded for a property when you open it. This
 * has forced the deletion of properties to only be possible in the
 * property page because that is the only point we know the users
 * permissions.
 */

function Properties(props) {
  // Data
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingPropertyKeyContacts, setLoadingPropertyKeyContacts] =
    useState(false);
  const [propertyKeyContacts, setPropertyKeyContacts] = useState([]);
  const [loadingPropertyRoles, setLoadingPropertyRoles] = useState(false);
  const [propertyRoles, setPropertyRoles] = useState([]);
  const addPropertyButtonRef = useRef();

  const [currentPage, setCurrentPage] = useState(0);

  const newProperty = props.newProperty;

  useEffect(() => {
    if (
      (!newProperty.data.name && !newProperty.data.identifer) ||
      !newProperty.redirect
    )
      return;
    if (addPropertyButtonRef) {
      addPropertyButtonRef.current.click();
    }
  }, []);

  // Display options
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  /**
   * Load the following when the bearer token is updated:
   *  - users (Used by property permissions and property key contacts)
   *  - regions
   *  - property key contacts
   *  - property roles
   */
  useEffect(() => {
    if (props.selectedDatabase) {
      // Load users
      setLoadingUsers(true);
      fetch(process.env.REACT_APP_AUTH_API_URL + 'api/users', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          service: props.authService
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setUsers(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
          setUsers([]);
        })
        .finally(() => {
          setLoadingUsers(false);
        });

      // Load regions
      setLoadingRegions(true);
      fetch(process.env.REACT_APP_PROPERTIES_API_URL + 'api/regions', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setRegions(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
          setRegions([]);
        })
        .finally(() => {
          setLoadingRegions(false);
        });

      // Load property key contacts
      setLoadingPropertyKeyContacts(true);
      fetch(
        process.env.REACT_APP_PROPERTIES_API_URL + 'api/property-key-contacts',
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + props.bearerToken,
            SeasonalDatabase: props.selectedDatabase
          }
        }
      )
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setPropertyKeyContacts(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
          setPropertyKeyContacts([]);
        })
        .finally(() => {
          setLoadingPropertyKeyContacts(false);
        });

      // Load property roles
      setLoadingPropertyRoles(true);
      fetch(process.env.REACT_APP_PROPERTIES_API_URL + 'api/property-roles', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setPropertyRoles(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
          setPropertyRoles([]);
        })
        .finally(() => {
          setLoadingPropertyRoles(false);
        });
    }
  }, [props.bearerToken, props.selectedDatabase]);

  /**
   * Update the property key contacts state array.
   *
   * @param {object} propertyKeyContact
   */
  const updatePropertyKeyContact = (propertyKeyContact) => {
    const index = propertyKeyContacts.findIndex(
      (p) => p.id === propertyKeyContact.id
    );

    // Update the property key contact
    if (index > -1) {
      const newPropertyKeyContacts = [...propertyKeyContacts];
      newPropertyKeyContacts[index] = propertyKeyContact;
      setPropertyKeyContacts(newPropertyKeyContacts);
    }

    // Add a new property key contact
    else {
      setPropertyKeyContacts([...propertyKeyContacts, propertyKeyContact]);
    }
  };

  /**
   * Sends an http request to the properties API to delete the property and removes it from
   * the properties state array.
   *
   * @param {int} propertyId
   */
  const deleteProperty = (propertyId) => {
    setLoading(true);
    fetch(
      process.env.REACT_APP_PROPERTIES_API_URL + 'api/properties/' + propertyId,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          /*
           * Properties use soft deletion, so update the property in the properties state array
           * with the new deleted_at field.
           */
          props.propertiesStore.updateProperty(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Overwrites a property in the properties state array.
   *
   * @param {object} property
   */
  const updateProperty = (property) => {
    updatePropertyKeyContact(property.property_key_contact);
    props.propertiesStore.updateProperty(property);
  };

  /**
   * Adds a property into the properies state array.
   *
   * @param {object} property
   */
  const addProperty = (property) => {
    updatePropertyKeyContact(property.property_key_contact);
    props.propertiesStore.addProperty(property);
    setShowAddPropertyModal(false);
    props.changeSelectedProperty(property.id);
  };

  /**
   * Use the supplied changeSelectedProperty() method in the props to close the selected property.
   */
  const closeSelectedProperty = () => {
    props.changeSelectedProperty(null);
  };

  // Check if anything is loading
  if (
    props.propertiesStore.loading ||
    loading ||
    loadingUsers ||
    loadingRegions ||
    loadingPropertyKeyContacts ||
    loadingPropertyRoles
  ) {
    return (
      <div className='h-100'>
        <LoadingOverlay hideOpacity />
      </div>
    );
  } else {
    // if (!props.propertiesStore.loadedPermissions) return null;

    const selectedProperty =
      props.propertiesStore.properties[
        props.propertiesStore.properties.findIndex(
          (element) => element.id === props.selectedPropertyId
        )
      ];

    // Check if a valid property has been selected
    if (selectedProperty) {
      return (
        <Property
          users={users}
          regions={regions}
          propertyKeyContacts={propertyKeyContacts}
          propertyRoles={propertyRoles}
          property={selectedProperty}
          update={updateProperty}
          delete={deleteProperty}
          close={closeSelectedProperty}
          history={props.history}
          {...props}
        />
      );
    }

    // Otherwise display a table of all properties
    else {
      return (
        <Fragment>
          {/* Add Property */}
          <Button
            ref={addPropertyButtonRef}
            variant='outline-success'
            className='mb-2'
            onClick={() => {
              setShowAddPropertyModal(true);
            }}
          >
            Add Property
          </Button>
          <Modal
            backdrop='static'
            show={showAddPropertyModal}
            onHide={() => {
              setShowAddPropertyModal(false);
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Add Property</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AddProperty
                users={users}
                regions={regions}
                propertyKeyContacts={propertyKeyContacts}
                add={addProperty}
                newProperty={newProperty}
                {...props}
              />
            </Modal.Body>
          </Modal>

          <PropertiesTable
            data={props.propertiesStore.properties}
            linkTo={props.linkTo}
            changeSelectedProperty={props.changeSelectedProperty}
            deleteProperty={deleteProperty}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </Fragment>
      );
    }
  }
}

Properties.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  authService: PropTypes.string.isRequired,

  // Properties
  propertiesStore: PropTypes.instanceOf(PropertiesStore),
  selectedPropertyId: PropTypes.number.isRequired,
  changeSelectedProperty: PropTypes.func.isRequired,

  // Helper methods
  linkTo: PropTypes.func.isRequired,

  newProperty: PropTypes.object.isRequired
};

export default observer(Properties);
